import {
  B2bCampaignAllocationDayType,
  B2bCampaignAllocationDayTypeValues,
} from '@Common/types/explore/employee_savings_campaign';

export type DayTypeAllocation = {
  days_type: B2bCampaignAllocationDayType;
  amount: number | null;
  // Days invested in this type, only set on rows coming from an uploaded file
  days?: number;
};

export const INVESTED_DAYS_FIELD_BY_TYPE = {
  cet: 'invested_cet_days',
  rtt: 'invested_rtt_days',
  overtime: 'invested_overtime_days',
  paid_leave: 'invested_paid_leave_days',
  seniority_leave: 'invested_seniority_leave_days',
  untaken_rest: 'invested_untaken_rest_days',
} as const satisfies Record<B2bCampaignAllocationDayType, string>;

// Day types the employee chose to invest in, which are the only ones the breakdown can use
export const getAvailableDayTypes = (
  allocation: Partial<
    Record<(typeof INVESTED_DAYS_FIELD_BY_TYPE)[B2bCampaignAllocationDayType], number | null>
  >,
) =>
  B2bCampaignAllocationDayTypeValues.filter(
    (dayType) => (allocation[INVESTED_DAYS_FIELD_BY_TYPE[dayType]] ?? 0) > 0,
  );

// The backend requires an amount on exactly the invested day types: none on another type, none missing
export const hasDayTypesMismatch = (
  fileDayTypes: B2bCampaignAllocationDayType[],
  investedDayTypes: B2bCampaignAllocationDayType[],
) =>
  fileDayTypes.length !== investedDayTypes.length ||
  fileDayTypes.some((dayType) => !investedDayTypes.includes(dayType));

// Per-type amount fields of the allocations list response
export const AMOUNT_FIELD_BY_TYPE = {
  cet: 'cet_amount',
  rtt: 'rtt_amount',
  overtime: 'overtime_amount',
  paid_leave: 'paid_leave_amount',
  seniority_leave: 'seniority_leave_amount',
  untaken_rest: 'untaken_rest_amount',
} as const satisfies Record<B2bCampaignAllocationDayType, string>;

export const DAYS_FIELD_BY_TYPE = {
  cet: 'cet_days',
  rtt: 'rtt_days',
  overtime: 'overtime_days',
  paid_leave: 'paid_leave_days',
  seniority_leave: 'seniority_leave_days',
  untaken_rest: 'untaken_rest_days',
} as const satisfies Record<B2bCampaignAllocationDayType, string>;

export const getInvestedDaysFromFile = (dayTypeAllocations: DayTypeAllocation[]) =>
  Object.fromEntries(
    dayTypeAllocations.map((entry) => [INVESTED_DAYS_FIELD_BY_TYPE[entry.days_type], entry.days]),
  ) as Partial<Record<(typeof INVESTED_DAYS_FIELD_BY_TYPE)[B2bCampaignAllocationDayType], number>>;

export const getDaysByType = (
  allocation: Parameters<typeof getAvailableDayTypes>[0] | undefined,
): Record<(typeof DAYS_FIELD_BY_TYPE)[B2bCampaignAllocationDayType], number | null> =>
  Object.fromEntries(
    B2bCampaignAllocationDayTypeValues.map((dayType) => {
      const invested = allocation?.[INVESTED_DAYS_FIELD_BY_TYPE[dayType]] ?? 0;
      return [DAYS_FIELD_BY_TYPE[dayType], invested > 0 ? invested : null];
    }),
  ) as Record<(typeof DAYS_FIELD_BY_TYPE)[B2bCampaignAllocationDayType], number | null>;

export type VacationDaysAllocationFormRow = {
  day_type_allocations?: DayTypeAllocation[];
  invested_cet_days?: number | null;
  invested_rtt_days?: number | null;
  invested_overtime_days?: number | null;
  invested_paid_leave_days?: number | null;
  invested_seniority_leave_days?: number | null;
  invested_untaken_rest_days?: number | null;
  nir?: string | null;
  ntt?: string | null;
  employee_id?: number;
  employee_exists?: boolean;
  // The file's day types differ from the ones the employee invested in
  day_types_mismatch?: boolean;
  amount?: number | null;
  cet_days?: number | null;
  rtt_days?: number | null;
  overtime_days?: number | null;
  paid_leave_days?: number | null;
  seniority_leave_days?: number | null;
  untaken_rest_days?: number | null;
};

// A blank amount counts as zero, as in the step validation
export const isZeroAmount = (amount: number | null | undefined) => (amount ?? 0) === 0;
